/**
 * UndoToast — Phase 4.2.d.
 *
 * Lightweight toast component rendered near the bottom of the chat spine
 * for every active undo descriptor returned by GET /api/undo. Polls every
 * 5 seconds (cheap; the route filters out undone + expired rows server-
 * side). Each toast shows a countdown until expires_at and dispatches
 * POST /api/undo/:descriptorId/execute on click.
 *
 * Visual conventions match the InlineApproval / onboarding-blocker
 * patterns in ChatSpine.tsx — `var(--c-*)` tokens for theming, rounded
 * corners, soft accent backgrounds. Toasts auto-dismiss when the
 * descriptor passes `expires_at` (the next poll drops it).
 *
 * Defensive design:
 *   - Polling errors swallow silently — operator-side observability is
 *     out of scope here.
 *   - 503 / 401 from the API → render nothing rather than red surface,
 *     matching the rest of the workspace which degrades quietly when
 *     Postgres-backed routes aren't ready in mock-mode dev.
 *   - Click triggers an optimistic dismissal so a slow server doesn't
 *     leave the toast lingering after the operator already moved on.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UndoDescriptorPayload {
  descriptorId: string;
  description: string;
  expiresAt: string;
  expiresInSeconds: number;
  reverseHandlerType: string;
}

interface UndoListResponse {
  descriptors: UndoDescriptorPayload[];
}

const POLL_INTERVAL_MS = 5000;

interface UndoToastProps {
  /** Override the polling interval (test affordance). */
  pollIntervalMs?: number;
  /** Override the fetch implementation (test affordance). */
  fetchImpl?: typeof fetch;
  /** Override `Date.now()` source (test affordance). */
  nowFn?: () => number;
}

export default function UndoToast({
  pollIntervalMs = POLL_INTERVAL_MS,
  fetchImpl,
  nowFn,
}: UndoToastProps = {}): React.ReactElement | null {
  const fetchFn = fetchImpl ?? (typeof fetch === "function" ? fetch : undefined);
  const [descriptors, setDescriptors] = useState<UndoDescriptorPayload[]>([]);
  const [tickMs, setTickMs] = useState<number>(() => (nowFn ? nowFn() : Date.now()));
  const dismissedRef = useRef<Set<string>>(new Set());

  const refreshNow = useMemo(() => nowFn ?? (() => Date.now()), [nowFn]);

  const poll = useCallback(async () => {
    if (!fetchFn) return;
    try {
      const res = await fetchFn("/api/undo");
      if (!res.ok) {
        // 401 / 503 / 4xx — degrade quietly. The chat spine still works.
        if (res.status === 401 || res.status === 503) {
          setDescriptors([]);
        }
        return;
      }
      const body = (await res.json()) as UndoListResponse;
      const incoming = Array.isArray(body.descriptors) ? body.descriptors : [];
      const filtered = incoming.filter((d) => !dismissedRef.current.has(d.descriptorId));
      setDescriptors(filtered);
    } catch {
      /* swallow */
    }
  }, [fetchFn]);

  useEffect(() => {
    let cancelled = false;
    void poll();
    const iv = setInterval(() => {
      if (!cancelled) void poll();
    }, pollIntervalMs);
    const tick = setInterval(() => {
      if (!cancelled) setTickMs(refreshNow());
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(iv);
      clearInterval(tick);
    };
  }, [poll, pollIntervalMs, refreshNow]);

  const handleUndo = useCallback(
    async (descriptorId: string) => {
      // Optimistic dismiss — even if the server is slow, the operator
      // shouldn't see the toast linger.
      dismissedRef.current.add(descriptorId);
      setDescriptors((prev) => prev.filter((d) => d.descriptorId !== descriptorId));
      if (!fetchFn) return;
      try {
        const res = await fetchFn(`/api/undo/${encodeURIComponent(descriptorId)}/execute`, {
          method: "POST",
        });
        if (!res.ok) {
          // Re-poll to refresh the visible toasts. If the descriptor is
          // genuinely gone (404 expired / undone) it stays dismissed; if
          // the handler errored 500, the row is still active and the
          // operator can retry from the next poll.
          dismissedRef.current.delete(descriptorId);
          await poll();
        }
      } catch {
        dismissedRef.current.delete(descriptorId);
        await poll();
      }
    },
    [fetchFn, poll],
  );

  // Filter out anything whose expires_at has elapsed since the last poll —
  // the server filters server-side too, but the second-by-second tick
  // means we drop them in the UI immediately.
  const visible = useMemo(() => {
    return descriptors.filter((d) => {
      const expiresMs = new Date(d.expiresAt).getTime();
      return Number.isFinite(expiresMs) && expiresMs > tickMs;
    });
  }, [descriptors, tickMs]);

  if (visible.length === 0) return null;

  return (
    <div
      data-testid="undo-toast-container"
      aria-live="polite"
      style={{
        position: "absolute",
        bottom: 96,
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "0 16px",
        pointerEvents: "none",
        zIndex: 30,
      }}
    >
      {visible.map((descriptor) => {
        const expiresMs = new Date(descriptor.expiresAt).getTime();
        const remaining = Math.max(0, Math.ceil((expiresMs - tickMs) / 1000));
        return (
          <div
            key={descriptor.descriptorId}
            data-testid="undo-toast"
            data-descriptor-id={descriptor.descriptorId}
            className="rounded-2xl px-4 py-2.5 max-w-md w-full flex items-center gap-3"
            style={{
              border: "1px solid var(--c-border)",
              backgroundColor: "var(--c-surface-elevated, var(--c-surface))",
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
              pointerEvents: "auto",
            }}
          >
            <span className="text-sm flex-1" style={{ color: "var(--c-text)" }}>
              Hank just {descriptor.description}
            </span>
            <span
              className="text-[11px] tabular-nums"
              style={{ color: "var(--c-text-faint)" }}
              data-testid="undo-toast-countdown"
            >
              {remaining}s
            </span>
            <button
              type="button"
              data-testid="undo-toast-button"
              onClick={() => {
                void handleUndo(descriptor.descriptorId);
              }}
              className="px-3 py-1 rounded-md text-xs font-semibold transition-colors"
              style={{
                border: "1px solid var(--c-border)",
                backgroundColor: "var(--c-surface)",
                color: "var(--c-text)",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = "var(--c-hover)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = "var(--c-surface)";
              }}
            >
              Undo
            </button>
          </div>
        );
      })}
    </div>
  );
}
